import { useT } from "@transifex/react";
import { useState } from "react";
import IconTextRow from "../../../basic/iconTextRow/IconTextRow";
import BasePopupWrapper, { TitleColorVariants } from "../../../basic/popups/BasePopupWrapper";
import { ButtonColorVariant, IconPosition } from "../../teacherArea/AreaButton";
import { FormattedText } from "../../teacherArea/FormattedText";

interface DeleteClassPopupProps {
    isOpen: boolean;
    schoolClassName: string;
    onClose: () => void;
    onConfirm: () => Promise<void>;
}

const DeleteClassPopup = (props: DeleteClassPopupProps) => {
    const t = useT();

    const [isSubmitting, setIsSubmitting] = useState(false);

    const t_deleteClass = t("Delete class", { _tags: "SchoolClasses, DeleteClassPopup" });
    const t_cancel = t("Cancel", { _tags: "SchoolClasses, DeleteClassPopup" });
    const t_delete = t("Delete", { _tags: "SchoolClasses, DeleteClassPopup" });
    const t_confirmDelete = t('Are you sure you want to delete <b>"{className}"</b>? This action cannot be undone.', {
        className: props.schoolClassName,
        _tags: "SchoolClasses, DeleteClassPopup",
    });
    const t_willBeDeleted = t("Will be deleted:", { _tags: "SchoolClasses, DeleteClassPopup" });
    const t_deleteItemActivities = t("All assigned tests, exercises and vocabulary trainings", {
        _tags: "SchoolClasses, DeleteClassPopup",
    });
    const t_deleteItemStudents = t("All students, including their answers and results", {
        _tags: "SchoolClasses, DeleteClassPopup",
    });
    const t_reassurance = t("Progress and accounts in the vocabulary app will be preserved.", {
        _tags: "SchoolClasses, DeleteClassPopup",
    });

    // Guards against a second click landing before the disabled state has rendered — each one would
    // run its own class deletion.
    const handleConfirm = async () => {
        if (isSubmitting) return;

        setIsSubmitting(true);
        try {
            await props.onConfirm();
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (isSubmitting) return;
        props.onClose();
    };

    return (
        <BasePopupWrapper
            isOpen={props.isOpen}
            onClose={handleClose}
            title={t_deleteClass}
            titleColor={TitleColorVariants.DANGER}
            titleIcon="alert"
            secondaryButton={{
                text: t_cancel,
                onClick: handleClose,
                disabled: isSubmitting,
                className: "min-w-[120px]",
            }}
            primaryButton={{
                text: t_delete,
                onClick: handleConfirm,
                disabled: isSubmitting,
                colorVariant: ButtonColorVariant.DANGER,
                icon: "trash",
                iconPosition: IconPosition.RIGHT,
                className: "min-w-[120px]",
            }}
        >
            <div className="flex flex-col gap-s">
                <FormattedText
                    rawText={t_confirmDelete}
                    textColor="text-l text-p6-grey-dark"
                />
                <div className="flex flex-col gap-xs">
                    <span className="text-l text-p6-grey-dark">{t_willBeDeleted}</span>
                    <div className="flex flex-col gap-xs pl-m">
                        <IconTextRow
                            icon="close"
                            text={t_deleteItemActivities}
                        />
                        <IconTextRow
                            icon="close"
                            text={t_deleteItemStudents}
                        />
                    </div>
                </div>

                <p className="text-l text-p6-grey-dark">{t_reassurance}</p>
            </div>
        </BasePopupWrapper>
    );
};

export default DeleteClassPopup;
